import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, MessageCircleHeart } from "lucide-react";
import { useAccount, photoUrl, errMsg } from "@/lib/account";

const Row = ({ r, incoming, onRespond }) => (
  <div className="card-royal rounded-2xl p-4 flex items-center gap-4" data-testid={`interest-row-${r.id}`}>
    <Link to={`/profile/${r.profile.id}`} className="w-14 h-14 rounded-full overflow-hidden shrink-0 border border-amber-500/30">
      {r.profile.photos?.[0] && <img src={photoUrl(r.profile.photos[0])} alt="" className="w-full h-full object-cover object-top" />}
    </Link>
    <div className="flex-1 min-w-0">
      <Link to={`/profile/${r.profile.id}`} className="font-display text-xl hover:text-amber-200">{r.profile.name}, {r.profile.age}</Link>
      <p className="text-xs text-slate-400">{r.profile.profession} · {r.profile.city}{r.profile.is_ai && " · AI Profile"}</p>
    </div>
    {incoming && r.status === "pending" ? (
      <div className="flex gap-2">
        <button onClick={() => onRespond(r.id, true)} data-testid={`interest-accept-${r.id}`} className="btn-gold p-2.5 rounded-full"><Check className="w-4 h-4" /></button>
        <button onClick={() => onRespond(r.id, false)} data-testid={`interest-decline-${r.id}`} className="btn-ghost p-2.5 rounded-full"><X className="w-4 h-4" /></button>
      </div>
    ) : r.status === "accepted" ? (
      <Link to={`/chat/${r.profile.id}`} data-testid={`interest-chat-${r.id}`} className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-1"><MessageCircleHeart className="w-4 h-4" /> Chat</Link>
    ) : <span className="text-xs uppercase tracking-widest text-slate-400">{r.status}</span>}
  </div>
);

export default function Interests() {
  const { authed } = useAccount();
  const [data, setData] = useState({ received: [], sent: [] });
  const [tab, setTab] = useState("received");
  const load = useCallback(() => authed("get", "/interests").then(setData).catch(() => {}), [authed]);
  useEffect(() => { load(); }, [load]);
  const respond = async (id, accept) => {
    try { await authed("post", `/interests/${id}/respond`, { accept }); toast.success(accept ? "Accepted — you can chat now" : "Declined"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const list = data[tab];
  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-14" data-testid="interests-page">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Interests</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Who's interested</h1>
      <p className="text-slate-400 mt-3 text-sm">Sending an interest is free. Once accepted, you can start chatting.</p>
      <div className="flex gap-2 mt-8">
        {["received", "sent"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`interests-tab-${t}`} className={`px-5 py-2 rounded-full text-sm capitalize ${tab === t ? "btn-gold" : "btn-ghost"}`}>{t} ({data[t].length})</button>
        ))}
      </div>
      <div className="space-y-3 mt-6">
        {list.length === 0 && <p className="text-slate-500 text-sm py-10 text-center" data-testid="interests-empty">Nothing here yet. <Link to="/browse" className="text-amber-300 underline">Browse profiles</Link></p>}
        {list.map((r) => <Row key={r.id} r={r} incoming={tab === "received"} onRespond={respond} />)}
      </div>
    </div>
  );
}
